import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { fieldErrorOf } from '../../lib/field-error';
import { EditSheet } from '../ui/EditSheet';
import { Field, field } from '../create/Field';
import { Button } from '../ui/Button';
import {
  StaleNotice,
  UnresolvedState,
  shownState,
  refreshFailed,
} from '../ui/QueryStates';
import { IconInventory } from '../icons';
import { useOnline } from '../ui/useConnection';
import { Notice } from '../ui/Badge';

/** An item/vendor number is separate from item pricing; saving does not change any saved PO. */
export function VendorPartEditor({
  itemId,
  itemName,
  onClose,
}: {
  itemId: string;
  itemName: string;
  onClose: () => void;
}) {
  const trpc = useTRPC(),
    qc = useQueryClient(),
    online = useOnline();
  const vendors = useQuery(trpc.purchasing.vendors.queryOptions());
  const [selected, setSelected] = useState<string | null>(null),
    [number, setNumber] = useState<string | null>(null);
  useEffect(() => {
    if (selected === null && vendors.data?.[0]) setSelected(vendors.data[0].id);
  }, [selected, vendors.data]);
  const vendorId = selected ?? '';
  const vendorAvailable = Boolean(vendors.data?.some((v) => v.id === vendorId));
  const mapping = useQuery({
    ...trpc.purchasing.vendorParts.queryOptions({ vendorId, itemIds: [itemId] }),
    enabled: Boolean(vendorId),
  });
  const vendorState = shownState(vendors, vendors.data),
    mappingState = shownState(mapping, mapping.data);
  const stale =
    refreshFailed(vendors, vendors.data) || refreshFailed(mapping, mapping.data);
  const original = mapping.data?.[0]?.vendorPartNumber ?? '',
    value = number ?? original,
    dirty = number !== null && number !== original;
  const ready =
    vendorState === 'ready' &&
    mappingState === 'ready' &&
    !stale &&
    Boolean(vendorId) &&
    vendorAvailable &&
    online;
  const save = useMutation(
    trpc.purchasing.vendorPartSet.mutationOptions({
      onSuccess: () => {
        void qc.invalidateQueries();
        onClose();
      },
    }),
  );
  return (
    <EditSheet
      open
      onClose={onClose}
      title={`Vendor part number · ${itemName}`}
      dirty={dirty}
      saving={save.isPending}
      saveDisabled={!ready || value.length > 120}
      error={save.error ? { message: fieldErrorOf(save.error.message) } : null}
      onSave={() => {
        if (ready && !save.isPending)
          save.mutate({ vendorId, itemId, vendorPartNumber: value, ...idem() });
      }}
    >
      <StaleNotice
        show={
          refreshFailed(vendors, vendors.data) || refreshFailed(mapping, mapping.data)
        }
        onRetry={() => {
          void vendors.refetch();
          void mapping.refetch();
        }}
      />
      {selected && !vendorAvailable && vendorState === 'ready' ? (
        <Notice tone="warn">
          This vendor is unavailable. Your input is kept; reset before choosing another
          vendor.
        </Notice>
      ) : null}
      {!online ? (
        <Notice tone="warn">Go online to save. Your input is kept.</Notice>
      ) : null}
      {vendorState !== 'ready' ? (
        <UnresolvedState
          state={vendorState}
          what="Vendors"
          icon={IconInventory}
          error={vendors.error}
          onRetry={() => void vendors.refetch()}
        />
      ) : vendors.data?.length === 0 ? (
        <p>Add a vendor in Purchasing first.</p>
      ) : (
        <>
          <Field label="Vendor">
            <select
              className={field}
              aria-label="Vendor"
              value={vendorId}
              disabled={dirty || save.isPending}
              onChange={(e) => {
                setSelected(e.target.value);
                setNumber(null);
              }}
            >
              {!vendorAvailable && selected ? (
                <option value={selected}>Selected vendor (unavailable)</option>
              ) : null}
              {vendors.data?.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </Field>
          {mappingState !== 'ready' ? (
            <UnresolvedState
              state={mappingState}
              what="Vendor part number"
              icon={IconInventory}
              error={mapping.error}
              onRetry={() => void mapping.refetch()}
            />
          ) : (
            <Field label="Vendor part number">
              <input
                className={field}
                value={value}
                maxLength={120}
                disabled={save.isPending}
                onChange={(e) => setNumber(e.target.value)}
                placeholder="No saved number"
              />
            </Field>
          )}
          {dirty ? (
            <Button
              variant="ghost"
              disabled={save.isPending}
              onClick={() => setNumber(null)}
            >
              Reset to saved number
            </Button>
          ) : null}
          <p className="text-xs text-muted-foreground">
            Leave it blank to clear this vendor’s number. New drafts use the saved number;
            existing orders keep their own copy. Save or reset before choosing another
            vendor.
          </p>
        </>
      )}
    </EditSheet>
  );
}
